import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { fetchOrgSettingsRequest, updateOrgSettingsRequest } from '../../../api/organizations/orgSlice';
import { showSnackbar } from '../../../api/snackbarSlice';
import BulkUploadEmailSettings from './BulkUploadEmailSettings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportsEmailSettings from './ReportsEmailSettings';

const emailSchema = yup.object({
  email: yup.string().email('Invalid email format').required('Email is required'),
});

const OrgNotificationSection = () => {
  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.org);

  const [emailNotifications, setEmailNotifications] = useState({
    send_email_to_seller: false,
    send_email_to_buyer: false,
    consolidation_document_failure_notification: false,
  });

  const [editableIndex, setEditableIndex] = useState(null);

  const {
    control,
    handleSubmit: validateEmail,
    formState: { errors, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(emailSchema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  useEffect(() => {
    dispatch(fetchOrgSettingsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (settings?.email_notifications) {
      setEmailNotifications(settings.email_notifications || {});
    }
  }, [settings]);

  const updateSettings = (updatedNotif = {}) => {
    dispatch(
      updateOrgSettingsRequest({
        updates: {
          email_notifications: {
            ...emailNotifications,
            ...updatedNotif,
          },
        },
      }),
    );

    dispatch(
      showSnackbar({
        open: true,
        message: 'Email settings updated successfully',
        severity: 'success',
      }),
    );

    setEditableIndex(null);
  };

  const handleToggleChange = (field) => {
    const updated = { ...emailNotifications, [field]: !emailNotifications[field] };
    setEmailNotifications(updated);
    updateSettings(updated);
  };

  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      <Typography variant="h6" sx={{ mb: '1.5rem', mt: '1.5rem' }}>
        Document Notifications
        <Divider />
      </Typography>

      <Grid container>
        <Grid size={4.5}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={emailNotifications.send_email_to_seller}
                  onChange={() => handleToggleChange('send_email_to_seller')}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {'Notify Document Sender'}
                  <Tooltip title="When enabled, the document creator and root user along with all sub-users for that business profile receive the approved e-invoice PDF and status via email post-IRBM acceptance.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={emailNotifications.send_email_to_buyer}
                  onChange={() => handleToggleChange('send_email_to_buyer')}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {'Notify Document Receiver'}
                  <Tooltip title="When enabled, the end buyer receives the approved e-invoice PDF via email post-IRBM acceptance.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={emailNotifications.consolidation_document_failure_notification}
                  onChange={() => handleToggleChange('consolidation_document_failure_notification')}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {'Notify on Consolidation Failure'}
                  <Tooltip title="When enabled, the root user along with all sub-users for that business profile will be notified on every document consolidation failed.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </FormGroup>
        </Grid>
      </Grid>

      <BulkUploadEmailSettings />
      <ReportsEmailSettings />
    </Paper>
  );
};

export default OrgNotificationSection;
